import React, { useEffect, useRef, useState, memo } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { decryptImageFromUrl } from 'hooks/useImageDecrypt';
import { setImageValue, selectImageValue } from 'store/encryptedImagesSlice';
import { RootState } from 'store';

import { Skeleton } from '@mui/material';

interface InterImage {
  videoHashId: string;
  encryptedImageUrl: string;
  decryptionKeys: any;
  navigateToVideo: (timestamp: number) => void;
  timestamp: number;
  secondaryViewSize: any;
  isExternal?: boolean;
}

function EncytedImage({
  videoHashId,
  encryptedImageUrl,
  decryptionKeys,
  navigateToVideo,
  timestamp,
  secondaryViewSize,
  isExternal
}: InterImage) {
  const compressed = false;
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  const decryptedImage = useSelector((state: RootState) => selectImageValue(state.encryptedImages, encryptedImageUrl));

  useEffect(() => {
    mountedRef.current = true;

    const decryptImage = async () => {
      if (decryptionKeys?.key) {
        const decrypted = await decryptImageFromUrl(encryptedImageUrl, decryptionKeys.key, decryptionKeys.iv);

        if (mountedRef.current) {
          setImage(decrypted);
          dispatch(setImageValue({ url: encryptedImageUrl, value: decrypted }));
          setLoading(false);
        }
      }
    };

    if (decryptedImage) {
      setImage(decryptedImage);
      setLoading(false);
    } else {
      decryptImage();
    }

    return () => {
      mountedRef.current = false; // Prevent state update if unmounted
    };
  }, [videoHashId, encryptedImageUrl, decryptionKeys?.key]);

  return (
    <>
      {!loading && image ? (
        <div
          key={encryptedImageUrl}
          className="bg-transparent group relative h-full w-full max-w-[360px] rounded-lg transition-opacity duration-800 ease-in-out cursor-pointer"
          style={{
            backgroundImage: `url("${image}")`,
            backgroundSize: 'contain',
            width: isMobile ? '100%' : compressed ? 320 : '100%',
            height: isMobile
              ? '210px'
              : compressed
              ? 176
              : secondaryViewSize < 400
              ? 140
              : isExternal
              ? '180px'
              : '210px'
          }}
          onClick={() => navigateToVideo(timestamp)}
        ></div>
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
      )}
    </>
  );
}

export default memo(EncytedImage);
